/**
 * MED-1442: the one place for the per-modality attendance copy.
 *
 * Group coaching is "GC", or "PG" when the cohort is peer led. A cohort never
 * shows both. 1:1 is "1:1" (intro and midpoint check-ins included) and
 * workshop is "W".
 */
import type {
  AttendanceByModality,
  AttendanceModality,
  GroupCoachingSubType,
  ModalityAttendance,
} from '../types';

/** Display order of the buckets. */
export const ATTENDANCE_MODALITIES: readonly AttendanceModality[] = [
  'group_coaching',
  'one_on_one',
  'workshop',
];

/** The short prefix: "GC", "PG", "1:1" or "W". */
export function modalityAttendancePrefix(
  modality: AttendanceModality,
  subType: GroupCoachingSubType | undefined
): string {
  switch (modality) {
    case 'group_coaching':
      return subType === 'peer_led' ? 'PG' : 'GC';
    case 'one_on_one':
      return '1:1';
    case 'workshop':
      return 'W';
  }
}

/** "GC attendance", "PG attendance", "1:1 attendance" or "W attendance". */
export function modalityAttendanceLabel(
  modality: AttendanceModality,
  subType: GroupCoachingSubType | undefined
): string {
  return `${modalityAttendancePrefix(modality, subType)} attendance`;
}

interface RowWithModalities {
  attendance_by_modality?: AttendanceByModality;
  group_coaching_sub_type?: GroupCoachingSubType;
}

/** The buckets that at least one row carries, in display order. */
export function modalitiesPresent(
  rows: readonly RowWithModalities[]
): AttendanceModality[] {
  return ATTENDANCE_MODALITIES.filter(k =>
    rows.some(r => r.attendance_by_modality?.[k])
  );
}

/** True when the object holds at least one bucket. `{}` and a missing object hold none. */
export function hasBuckets(
  abm: AttendanceByModality | undefined
): abm is AttendanceByModality {
  return !!abm && Object.keys(abm).length > 0;
}

/**
 * Show the GC columns unless the rows carry buckets and none is group
 * coaching (a 1:1-only cohort). Rows with no buckets (an older API) keep GC.
 */
export function showsGroupCoaching(rows: readonly RowWithModalities[]): boolean {
  const withBuckets = rows.filter(r => hasBuckets(r.attendance_by_modality));
  return (
    withBuckets.length === 0 ||
    withBuckets.some(r => r.attendance_by_modality?.group_coaching)
  );
}

/** The group coaching sub type of the rows (all rows share one cohort). */
export function groupCoachingSubTypeOf(
  rows: readonly RowWithModalities[]
): GroupCoachingSubType {
  return (
    rows.find(r => r.group_coaching_sub_type)?.group_coaching_sub_type ?? null
  );
}

/** A bucket in the shape of the legacy attendance fields, for the shared cell renderers. */
export function bucketAsAttendanceFields(
  bucket: ModalityAttendance | undefined
): {
  attendance_percentage: number | null;
  sessions_attended: number;
  total_sessions: number;
  attendance_population: number;
} {
  return {
    attendance_percentage: bucket?.percentage ?? null,
    sessions_attended: (bucket?.attended ?? 0) + (bucket?.partial ?? 0),
    total_sessions: bucket?.marked ?? 0,
    attendance_population: bucket?.population ?? 0,
  };
}

/** MED-1442: the Data Library columns that count group coaching only. */
const GROUP_COACHING_COLUMN_LABELS: Record<string, string> = {
  attendance: 'Attendance %',
  attendance_coverage: 'Attendance Coverage',
  attendance_detail: 'Attendance Detail',
};

/** MED-1442: the group coaching label the API gives for a set of cohorts. */
export type GroupCoachingPrefix = 'GC' | 'PG' | 'GC/PG';

/**
 * Label the group coaching columns with the prefix the API computed over the
 * whole filtered set ("GC" until the first response). Columns key on id, so
 * saved layouts keep.
 */
export function labelGroupCoachingColumns<C extends { id: string; label: string }>(
  columns: readonly C[],
  prefix: GroupCoachingPrefix | undefined
): C[] {
  return columns.map(c =>
    GROUP_COACHING_COLUMN_LABELS[c.id]
      ? { ...c, label: `${prefix ?? 'GC'} ${GROUP_COACHING_COLUMN_LABELS[c.id]}` }
      : c
  );
}

/** "4 of 4 attended, 1 partial": attended includes partial, as in the rate. */
export function attendedHeadline(attended: number, marked: number, partial: number): string {
  return `${attended} of ${marked} attended${partial > 0 ? `, ${partial} partial` : ''}`;
}
